import React, { useState } from 'react';
import { useData, NotificationLog } from '../../contexts/DataContext';
import { formatDate } from '../../utils/mockData';
import { MessageSquareIcon, MailIcon, SendIcon, RefreshCwIcon, AlertTriangleIcon } from 'lucide-react';
const NotificationsPage: React.FC = () => {
  const {
    sendSMS,
    sendEmail,
    notificationLogs,
    contacts
  } = useData();
  const [smsMessage, setSmsMessage] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [isSendingSMS, setIsSendingSMS] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const handleSendSMS = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!smsMessage.trim()) return;
    setIsSendingSMS(true);
    try {
      await sendSMS(smsMessage);
      setSmsMessage('');
    } catch (error) {
      console.error('Failed to send SMS:', error);
    } finally {
      setIsSendingSMS(false);
    }
  };
  const handleSendEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailSubject.trim() || !emailMessage.trim()) return;
    setIsSendingEmail(true);
    try {
      await sendEmail(emailSubject, emailMessage);
      setEmailSubject('');
      setEmailMessage('');
    } catch (error) {
      console.error('Failed to send email:', error);
    } finally {
      setIsSendingEmail(false);
    }
  };
  // Group logs by date
  const groupedLogs: Record<string, NotificationLog[]> = {};
  notificationLogs.forEach(log => {
    const date = new Date(log.timestamp).toLocaleDateString();
    if (!groupedLogs[date]) {
      groupedLogs[date] = [];
    }
    groupedLogs[date].push(log);
  });
  // Sort dates in descending order
  const sortedDates = Object.keys(groupedLogs).sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
  return <div className="space-y-6" data-id="element-231">
      <h2 className="text-2xl font-bold" data-id="element-232">Notifications</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-233">
        {/* SMS Form */}
        <div className="bg-white rounded-lg shadow p-4" data-id="element-234">
          <div className="flex items-center mb-4" data-id="element-235">
            <MessageSquareIcon className="h-5 w-5 text-blue-600 mr-2" data-id="element-236" />
            <h3 className="text-lg font-semibold" data-id="element-237">Send SMS Notification</h3>
          </div>
          <form onSubmit={handleSendSMS} data-id="element-238">
            <div className="mb-4" data-id="element-239">
              <label htmlFor="smsMessage" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-240">
                Message
              </label>
              <textarea id="smsMessage" rows={4} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Type your SMS message here..." value={smsMessage} onChange={e => setSmsMessage(e.target.value)} required data-id="element-241" />
            </div>
            <div className="flex items-center justify-between" data-id="element-242">
              <div className="text-sm text-gray-500" data-id="element-243">
                Recipients: {contacts.length} contacts
              </div>
              <button type="submit" className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors" disabled={isSendingSMS} data-id="element-244">
                {isSendingSMS ? <>
                    <RefreshCwIcon className="h-4 w-4 mr-2 animate-spin" data-id="element-245" />
                    Sending...
                  </> : <>
                    <SendIcon className="h-4 w-4 mr-2" data-id="element-246" />
                    Send SMS
                  </>}
              </button>
            </div>
          </form>
        </div>
        {/* Email Form */}
        <div className="bg-white rounded-lg shadow p-4" data-id="element-247">
          <div className="flex items-center mb-4" data-id="element-248">
            <MailIcon className="h-5 w-5 text-green-600 mr-2" data-id="element-249" />
            <h3 className="text-lg font-semibold" data-id="element-250">Send Email Notification</h3>
          </div>
          <form onSubmit={handleSendEmail} data-id="element-251">
            <div className="mb-3" data-id="element-252">
              <label htmlFor="emailSubject" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-253">
                Subject
              </label>
              <input type="text" id="emailSubject" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Email subject" value={emailSubject} onChange={e => setEmailSubject(e.target.value)} required data-id="element-254" />
            </div>
            <div className="mb-4" data-id="element-255">
              <label htmlFor="emailMessage" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-256">
                Message
              </label>
              <textarea id="emailMessage" rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Type your email message here..." value={emailMessage} onChange={e => setEmailMessage(e.target.value)} required data-id="element-257" />
            </div>
            <div className="flex items-center justify-between" data-id="element-258">
              <div className="text-sm text-gray-500" data-id="element-259">
                Recipients: {contacts.length} contacts
              </div>
              <button type="submit" className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors" disabled={isSendingEmail} data-id="element-260">
                {isSendingEmail ? <>
                    <RefreshCwIcon className="h-4 w-4 mr-2 animate-spin" data-id="element-261" />
                    Sending...
                  </> : <>
                    <SendIcon className="h-4 w-4 mr-2" data-id="element-262" />
                    Send Email
                  </>}
              </button>
            </div>
          </form>
        </div>
      </div>
      {/* Notification Logs */}
      <div className="bg-white rounded-lg shadow p-4" data-id="element-263">
        <h3 className="text-lg font-semibold mb-4" data-id="element-264">Notification History</h3>
        {sortedDates.length === 0 ? <div className="text-center py-6 text-gray-500" data-id="element-265">
            No notifications have been sent yet.
          </div> : <div className="space-y-4" data-id="element-266">
            {sortedDates.map(date => <div key={date} data-id="element-267">
                <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-268">
                  {date}
                </h4>
                <div className="space-y-2" data-id="element-269">
                  {groupedLogs[date].map(log => <div key={log.id} className={`p-3 rounded-lg border-l-4 ${log.type === 'sms' ? 'bg-blue-50 border-blue-500' : log.type === 'email' ? 'bg-green-50 border-green-500' : 'bg-yellow-50 border-yellow-500'}`} data-id="element-270">
                      <div className="flex items-start" data-id="element-271">
                        <div className={`p-1 rounded-full mr-2 ${log.type === 'sms' ? 'bg-blue-100 text-blue-600' : log.type === 'email' ? 'bg-green-100 text-green-600' : 'bg-yellow-100 text-yellow-600'}`} data-id="element-272">
                          {log.type === 'sms' && <MessageSquareIcon className="h-4 w-4" data-id="element-273" />}
                          {log.type === 'email' && <MailIcon className="h-4 w-4" data-id="element-274" />}
                          {log.type === 'auto-alert' && <AlertTriangleIcon className="h-4 w-4" data-id="element-275" />}
                        </div>
                        <div className="flex-1" data-id="element-276">
                          <div className="flex justify-between" data-id="element-277">
                            <span className="text-sm font-medium" data-id="element-278">
                              {log.type === 'sms' && 'SMS Notification'}
                              {log.type === 'email' && 'Email Notification'}
                              {log.type === 'auto-alert' && 'Automatic Alert'}
                            </span>
                            <span className="text-xs text-gray-500" data-id="element-279">
                              {new Date(log.timestamp).toLocaleTimeString()}
                            </span>
                          </div>
                          <p className="text-sm mt-1" data-id="element-280">{log.message}</p>
                          <div className="text-xs text-gray-500 mt-1" data-id="element-281">
                            Sent to {log.recipients} recipients
                          </div>
                        </div>
                      </div>
                    </div>)}
                </div>
              </div>)}
          </div>}
      </div>
      {/* Recipients List */}
      <div className="bg-white rounded-lg shadow p-4" data-id="element-282">
        <h3 className="text-lg font-semibold mb-4" data-id="element-283">Notification Recipients</h3>
        <div className="overflow-x-auto" data-id="element-284">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-285">
            <thead className="bg-gray-50" data-id="element-286">
              <tr data-id="element-287">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-288">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-289">
                  Phone
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-290">
                  Email
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-291">
              {contacts.map(contact => <tr key={contact.id} data-id="element-292">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-293">
                    {contact.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-294">
                    {contact.phone}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-295">
                    {contact.email}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
};
export default NotificationsPage;